'use client';

import { c as _c } from "react/compiler-runtime";
import useUser from '@shared/utils/hooks/useUser';
import { AnonymousAvatar, PrivatePostAvatar } from '@shared/utils/icons';
import { ChevronDown, Info } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import usePresence from '../../../hooks/usePresence';
import { animDelay } from '../../../utils/animDelay';
import Avatar from '../../common/Avatar';
import { AnonymousPostExplanationModal } from '../AnonymousPostExplanationModal';
import { hasSeenPrivatePostModal, PrivatePostExplanationModal } from '../PrivatePostExplanationModal';
import { useNewPostContext } from './NewPostContext';
type PostMode = 'public' | 'anonymous' | 'local';
type Props = {
  hidePrivate?: boolean;
  parentIsPrivate?: boolean;
  size?: number;
  className?: string;
};
const PAD = 6;
const GAP = 4;
export const AvatarWithSettings = t0 => {
  const $ = _c(26);
  const {
    hidePrivate,
    parentIsPrivate,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? 35 : t1;
  const {
    currentUser
  } = useUser();
  const {
    anonymousPost,
    setAnonymousPost,
    privatePost,
    setPrivatePost
  } = useNewPostContext();
  const [isOpen, setIsOpen] = useState(false);
  const popover = usePresence(isOpen, 150);
  const [showPrivateExplanation, setShowPrivateExplanation] = useState(false);
  const [showAnonymousExplanation, setShowAnonymousExplanation] = useState(false);
  const avatarRef = useRef(null);
  const portalRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 0,
      left: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [pos, setPos] = useState(t2);
  const currentMode = privatePost ? "local" : anonymousPost ? "anonymous" : "public";
  const options = [{
    mode: "public" as PostMode,
    label: "As Yourself",
    show: !parentIsPrivate,
    renderIcon: s => <Avatar avatarKey={currentUser.avatarKey} robohash={currentUser.robohash} isBot={currentUser.tag === "BOT"} size={s} userId={currentUser.id} />
  }, {
    mode: "anonymous" as PostMode,
    label: "Anonymous",
    show: !parentIsPrivate,
    info: true,
    renderIcon: _temp
  }, {
    mode: "local" as PostMode,
    label: "Local",
    show: !hidePrivate,
    info: true,
    renderIcon: _temp2
  }];
  const visibleOptions = options.filter(_temp3);
  const activeIndex = visibleOptions.findIndex(o_0 => o_0.mode === currentMode);
  const currentOption = visibleOptions[activeIndex];
  const collapsedH = size + PAD * 2;
  const expandedH = visibleOptions.length * size + (visibleOptions.length - 1) * GAP + PAD * 2;
  let t3;
  if ($[1] !== activeIndex || $[2] !== pos || $[3] !== size) {
    t3 = () => {
      if (!avatarRef.current) {
        return pos;
      }
      const rect = avatarRef.current.getBoundingClientRect();
      return {
        top: rect.top - PAD - activeIndex * (size + GAP),
        left: rect.left - PAD
      };
    };
    $[1] = activeIndex;
    $[2] = pos;
    $[3] = size;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const getPosition = t3;
  let t4;
  if ($[5] !== getPosition || $[6] !== isOpen) {
    t4 = () => {
      if (!isOpen) {
        return;
      }
      setPos(getPosition());
      const handler = e => {
        if (avatarRef.current && !avatarRef.current.contains(e.target as Node) && portalRef.current && !portalRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => {
        document.removeEventListener("mousedown", handler);
      };
    };
    $[5] = getPosition;
    $[6] = isOpen;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== activeIndex || $[9] !== isOpen || $[10] !== size) {
    t5 = [isOpen, activeIndex, size];
    $[8] = activeIndex;
    $[9] = isOpen;
    $[10] = size;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useLayoutEffect(t4, t5);
  const selectMode = mode => {
    if (mode !== currentMode) {
      bb52: switch (mode) {
        case "public":
          {
            setAnonymousPost(false);
            setPrivatePost(false);
            break bb52;
          }
        case "anonymous":
          {
            setAnonymousPost(true);
            setPrivatePost(false);
            break bb52;
          }
        case "local":
          {
            setAnonymousPost(false);
            setPrivatePost(true);
            if (!hasSeenPrivatePostModal()) {
              setShowPrivateExplanation(true);
            }
          }
      }
    }
    setIsOpen(false);
  };
  let t6;
  if ($[12] !== size) {
    t6 = {
      width: size
    };
    $[12] = size;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== getPosition || $[15] !== isOpen) {
    t7 = () => {
      if (!isOpen) {
        setPos(getPosition());
      }
      setIsOpen(!isOpen);
    };
    $[14] = getPosition;
    $[15] = isOpen;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== size) {
    t8 = {
      width: size,
      height: size
    };
    $[17] = size;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = `absolute -bottom-1 -right-1 z-10 flex size-4 items-center justify-center rounded-full border border-ds-border-subtle bg-background-secondary text-textSecondary shadow-sm transition-all hover:bg-background-primary hover:text-textPrimary ${isOpen ? "rotate-180" : ""}`;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ChevronDown className="!size-2.5" />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== showPrivateExplanation) {
    t12 = showPrivateExplanation && <PrivatePostExplanationModal closeHandler={() => setShowPrivateExplanation(false)} />;
    $[22] = showPrivateExplanation;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== showAnonymousExplanation) {
    t13 = showAnonymousExplanation && <AnonymousPostExplanationModal closeHandler={() => setShowAnonymousExplanation(false)} />;
    $[24] = showAnonymousExplanation;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return <><div ref={avatarRef} className={`relative shrink-0 ${className ?? ""}`} style={t6}><div className="relative cursor-pointer" onClick={t7}><div className="overflow-hidden rounded-full" style={t8}>{currentOption?.renderIcon(size)}</div>{t11}</div></div>{typeof document !== "undefined" && createPortal(popover.rendered ? <div ref={portalRef} className={`fixed z-popover overflow-hidden rounded-2xl border border-ds-border-subtle bg-background-primary shadow-[0_4px_24px_rgba(0,0,0,0.4)] ${popover.exiting ? "anim-shrink" : "anim-grow"}`} style={{
      top: pos.top,
      left: pos.left,
      padding: PAD,
      height: popover.exiting ? collapsedH : expandedH,
      "--grow-from": `${collapsedH}px`
    } as CSSProperties}><div className="flex flex-col" style={{
        gap: GAP
      }}>{visibleOptions.map((option, i) => {
          const isActive = option.mode === currentMode;
          return <button key={option.mode} type="button" className={`group flex items-center gap-2 rounded-full pr-2 transition-colors [--enter-duration:150ms] hover:bg-background-light/50 ${isActive ? "" : "anim-enter"}`} style={{
            height: size,
            ...animDelay(i * 40)
          }} onClick={() => {
            if (!isActive) {
              selectMode(option.mode);
            } else {
              setIsOpen(false);
            }
          }}><div className="flex shrink-0 items-center justify-center overflow-hidden rounded-full" style={{
              width: size,
              height: size
            }}>{option.renderIcon(size)}</div><span className={`whitespace-nowrap text-xs ${isActive ? "font-emphasis text-textPrimary" : "text-textSecondary"}`}>{option.label}</span>{option.info && <Info className="size-3 shrink-0 text-textSecondary transition-colors hover:text-textPrimary" onClick={e_0 => {
              e_0.stopPropagation();
              setIsOpen(false);
              if (option.mode === "anonymous") {
                setShowAnonymousExplanation(true);
              } else {
                setShowPrivateExplanation(true);
              }
            }} />}</button>;
        })}</div></div> : null, document.body)}{t12}{t13}</>;
};
function _temp(s_0) {
  return <AnonymousAvatar size={s_0} />;
}
function _temp2(s_1) {
  return <PrivatePostAvatar size={s_1} />;
}
function _temp3(o) {
  return o.show;
}